import { Outlet, Link, useLocation, useParams } from "react-router-dom";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useState } from "react";
import { useEnsureActiveCompany } from "@/hooks/use-ensure-active-company";
import { useCompanyContext } from "@/hooks/use-company-context";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Truck,
  Users,
  MapPin,
  Fuel,
  Wrench,
  AlertTriangle,
  FileText,
  DollarSign,
  Settings,
  BarChart3,
  Route,
  CircuitBoard,
  Building2,
  Calendar,
  PieChart,
  HelpCircle,
  ChevronDown,
  Activity,
  TrendingUp,
  CircleDashed,
  Shield
} from "lucide-react";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useFleetPermissions } from "@/hooks/use-fleet-permissions";
import { useUnreadMarkingNotifications } from "@/hooks/use-marking-notifications";
import { UserHeaderMenu } from "@/components/UserHeaderMenu";
import { NotificationCenter } from "@/components/notifications/NotificationCenter";

const getFleetNavItems = (basePath: string, isFleetManager: boolean, isFleetOpsManager: boolean, unreadCount: number) => [
  {
    title: "Dashboard",
    url: basePath,
    icon: BarChart3,
  },
  {
    title: "Aprovações",
    url: `${basePath}/manager`,
    icon: Shield,
  },
  ...(isFleetOpsManager ? [{
    title: "Operações",
    url: `${basePath}/ops`,
    icon: Activity,
    badge: unreadCount > 0 ? unreadCount : undefined,
  }] : []),
  {
    title: "Veículos",
    url: `${basePath}/vehicles`,
    icon: Truck,
  },
  {
    title: "Motoristas",
    url: `${basePath}/drivers`,
    icon: Users,
  },
  {
    title: "Alocações",
    url: `${basePath}/assignments`,
    icon: CircuitBoard,
  },
  {
    title: "Viagens",
    url: `${basePath}/trips`,
    icon: Route,
  },
  {
    title: "Análise de Viagens",
    url: `${basePath}/trips-analysis`,
    icon: BarChart3,
  },
  {
    title: "Combustível",
    url: `${basePath}/fuel`,
    icon: Fuel,
  },
  {
    title: "Manutenção",
    url: `${basePath}/maintenance`,
    icon: Wrench,
  },
  {
    title: "Pneus",
    url: `${basePath}/tires`,
    icon: CircleDashed,
  },
  {
    title: "Documentos",
    url: `${basePath}/documents`, 
    icon: FileText,
  },
  {
    title: "Alertas",
    url: `${basePath}/alerts`,
    icon: AlertTriangle,
  },
  {
    title: "Fornecedores",
    url: `${basePath}/providers`,
    icon: Building2,
  },
  {
    title: "Custos",
    url: `${basePath}/costs`,
    icon: DollarSign,
  },
  {
    title: "Despesas",
    url: `${basePath}/expenses`,
    icon: FileText,
  },
  {
    title: "Configurações",
    url: `${basePath}/settings`,
    icon: Settings,
  },
];

const getReportsSubItems = (basePath: string) => [
  {
    title: "Dashboard",
    url: `${basePath}/reports/dashboard`,
    icon: BarChart3,
  },
  {
    title: "Veículos",
    url: `${basePath}/reports/vehicles`,
    icon: Truck,
  },
  {
    title: "Motoristas",
    url: `${basePath}/reports/drivers`,
    icon: Users,
  },
  {
    title: "Manutenções",
    url: `${basePath}/reports/maintenance`,
    icon: Wrench,
  },
  {
    title: "Centros de Custo",
    url: `${basePath}/reports/cost-centers`,
    icon: Building2,
  },
  {
    title: "Financeiro",
    url: `${basePath}/reports/financial`,
    icon: DollarSign,
  },
  {
    title: "Operacional",
    url: `${basePath}/reports/operational`,
    icon: Activity,
  },
  {
    title: "Análise Comparativa",
    url: `${basePath}/reports/comparative`,
    icon: TrendingUp,
  },
];

function FleetSidebar({ 
  basePath, 
  isFleetManager, 
  isFleetOpsManager, 
  unreadCount 
}: { 
  basePath: string; 
  isFleetManager: boolean;
  isFleetOpsManager: boolean;
  unreadCount: number;
}) {
  const location = useLocation();
  const currentPath = location.pathname;
  const [reportsOpen, setReportsOpen] = useState(
    currentPath.includes('/reports')
  );
  
  const fleetNavItems = getFleetNavItems(basePath, isFleetManager, isFleetOpsManager, unreadCount);
  const reportsSubItems = getReportsSubItems(basePath);

  const isActive = (path: string) => {
    if (path === basePath) {
      return currentPath === basePath;
    }
    return currentPath.startsWith(path);
  };

  const isReportsActive = currentPath.includes('/reports');

  return (
    <Sidebar className="border-r bg-background">
      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel className="flex items-center gap-2 px-4 py-2">
            <div className="flex h-6 w-6 items-center justify-center rounded bg-primary">
              <Truck className="h-3 w-3 text-primary-foreground" />
            </div>
            FleetONE
            <Badge className="ml-auto bg-green-500 text-white hover:bg-green-600">
              v1.1
            </Badge>
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Itens normais de navegação */}
              {fleetNavItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild isActive={isActive(item.url)}>
                    <Link to={item.url} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <item.icon className="h-4 w-4" />
                        <span>{item.title}</span>
                      </div>
                      {(item as any).badge && (
                        <Badge variant="secondary" className="ml-auto text-xs">
                          {(item as any).badge}
                        </Badge>
                      )}
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}

              {/* Dropdown de Relatórios */}
              <SidebarMenuItem>
                <Collapsible
                  open={reportsOpen}
                  onOpenChange={setReportsOpen}
                  className="group/collapsible"
                >
                  <CollapsibleTrigger asChild>
                    <SidebarMenuButton
                      className="flex items-center justify-between w-full"
                      isActive={isReportsActive}
                    >
                      <div className="flex items-center gap-3">
                        <PieChart className="h-4 w-4" />
                        <span>Relatórios</span>
                      </div>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          reportsOpen && "rotate-180"
                        )}
                      />
                    </SidebarMenuButton>
                  </CollapsibleTrigger>

                  <CollapsibleContent className="space-y-1 pr-2 pt-1">
                    <SidebarMenu>
                      {reportsSubItems.map((item) => (
                        <SidebarMenuItem key={item.title}>
                          <SidebarMenuButton 
                            asChild 
                            isActive={isActive(item.url)}
                            className="text-sm"
                          >
                            <Link to={item.url} className="flex items-center gap-2">
                              <item.icon className="h-3.5 w-3.5" />
                              <span>{item.title}</span>
                            </Link>
                          </SidebarMenuButton>
                        </SidebarMenuItem>
                      ))}
                    </SidebarMenu>
                  </CollapsibleContent>
                </Collapsible>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
      
      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton asChild className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to={`${basePath}/support`}>
                <HelpCircle className="h-4 w-4" />
                <span>Suporte</span>
              </Link>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

export default function FleetLayout() {
  const { hasGlobalRole, hasCompanyRole, activeCompany } = useAuth();
  const { slug } = useParams<{ slug: string }>();
  const location = useLocation();
  
  // Garantir sincronização do activeCompany a partir do slug
  useEnsureActiveCompany();
  const { companyId, loading } = useCompanyContext();
  const { canView, isViewer, isFleetManager, isFleetOpsManager } = useFleetPermissions();
  const { data: unreadNotifications = [] } = useUnreadMarkingNotifications(activeCompany?.id);
  
  // Verificar se é fleet_manager
  const isFleetManagerRole = activeCompany && (
    hasCompanyRole(activeCompany.id, 'fleet_manager') ||
    hasCompanyRole(activeCompany.id, 'company_admin')
  );
  
  // Determine if we're in company context or standalone app
  const isCompanyContext = location.pathname.includes('/company/');
  const isGpsTracking = location.pathname.includes('/systems/gps_tracking');
  const isUserArea = location.pathname.includes('/user/systems/');
  const isAdminArea = location.pathname.includes('/admin/systems/');
  
  let basePath = '/app/fleet';
  if (isCompanyContext) {
    if (isUserArea) {
      basePath = isGpsTracking ? `/company/${slug}/user/systems/gps_tracking` : `/company/${slug}/user/systems/fleet`;
    } else if (isAdminArea) {
      basePath = isGpsTracking ? `/company/${slug}/admin/systems/gps_tracking` : `/company/${slug}/admin/systems/fleet`;
    } else {
      basePath = isGpsTracking ? `/company/${slug}/systems/gps_tracking` : `/company/${slug}/systems/fleet`;
    }
  }
  
  // Se estamos em contexto de empresa mas companyId ainda não foi carregado, mostrar skeleton
  if (slug && !companyId && !loading) {
    return (
      <div className="flex h-screen">
        <div className="w-64 border-r bg-sidebar p-4">
          <Skeleton className="h-8 w-full mb-4" />
          <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-6 w-3/4 mb-2" />
          <Skeleton className="h-6 w-3/4 mb-2" />
        </div>
        <div className="flex-1 p-8">
          <Skeleton className="h-10 w-1/3 mb-4" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  // If within user area or admin area, don't render sidebar (parent layout handles it)
  if (isUserArea || isAdminArea) {
    return (
      <div className="space-y-6">
        <Outlet />
      </div>
    );
  }

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <FleetSidebar 
          basePath={basePath} 
          isFleetManager={!!isFleetManagerRole} 
          isFleetOpsManager={isFleetOpsManager}
          unreadCount={unreadNotifications.length}
        />
        <main className="flex-1">
          {/* Header */}
          <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
            <div className="flex h-14 items-center gap-4 px-6">
              <SidebarTrigger />
              <div className="flex items-center gap-2">
                <h1 className="font-semibold">Gestão de Frotas</h1>
              </div>
              <div className="ml-auto flex items-center gap-2">
                <NotificationCenter />
                <UserHeaderMenu />
              </div>
            </div>
          </header>
          
          {/* Main Content */}
          <div className="p-6">
            <Outlet />
          </div>
        </main>
      </div>
    </SidebarProvider>
  );
}